import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router';
import { Button, Card, Heading, Layout, PageWrap } from '@vwfs-bronson/bronson-react';
import { getQuote } from '../../../../services/api/miles/miles-services';
import { useRemoveCardTitle } from '../../../../services/common/useRemoveCardTitle';
import { RootState, store } from '../../../../services/redux';
import { loadingActions } from '../../../../services/redux/features/loading.slice';
import { useNavigate } from '../../../../services/routing/navigate';
import { offerStatusIdOptionsList } from '../../constants/StatusMap';
import { DashboardDocumentLeasingDocumentsTable } from '../components/DashboardDocumentLeasingDocumentsTable/DashboardDocumentLeasingDocumentsTable';
import { DashboardDocumentUpload } from '../components/DashboardDocumentUpload/DashboardDocumentUpload';

export enum DashboardSubpageDocumentsSourceType {
  QUOTATION_SUMMARY = 'QUOTATION_SUMMARY',
  STUDIES = 'STUDIES',
  ACCEPTED = 'ACCEPTED',
  RUNNING = 'RUNNING',
}

export enum DashboardSubpageDocumentsEntityType {
  CUSTOMER_DOCUMENTS = 'CUSTOMER_DOCUMENTS',
  DEALER_DOCUMENTS = 'DEALER_DOCUMENTS',
  LEASING_DOCUMENTS = 'LEASING_DOCUMENTS',
}

export interface DashboardSubpageDocumentsProps {
  sourceType: DashboardSubpageDocumentsSourceType;
}

export const DashboardSubpageDocuments: FunctionComponent<DashboardSubpageDocumentsProps> = ({ sourceType }) => {
  const { t } = useTranslation();
  useRemoveCardTitle();

  const { id } = useParams();
  const [quote, setQuote] = useState<QuoteSchema.Schemas.FinancialProductOfferGetQuote | null>(null);
  const [quoteStatus, setQuoteStatus] = useState('');

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const contractId = searchParams.get('contractId');
  const offerId = searchParams.get('offerId');
  const transactionId = searchParams.get('cid');
  const contractStatus = searchParams.get('contractStatus') || '';
  const contractNumberAcceptedPage = useSelector((state: RootState) => state.dashboard.contractsNumbersAcceptedPage);

  // Id used for leasing document query depends on source type
  const leasingDocumentsSelectedId = [
    DashboardSubpageDocumentsSourceType.ACCEPTED,
    DashboardSubpageDocumentsSourceType.RUNNING,
  ].includes(sourceType)
    ? contractId
    : id;

  const getId = () => {
    if (sourceType === DashboardSubpageDocumentsSourceType.ACCEPTED) {
      return contractNumberAcceptedPage;
    }

    if (sourceType === DashboardSubpageDocumentsSourceType.RUNNING) {
      return offerId;
    }

    return quote?.offerReference;
  };

  useEffect(
    function onInvalidQuoteIdNavigateToErrorPage() {
      if (!id) {
        navigate.error();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id]
  );

  useEffect(
    function getQuoteData() {
      if (sourceType === DashboardSubpageDocumentsSourceType.RUNNING) {
        return;
      }

      const getQuoteData = async () => {
        if (id) {
          store.dispatch(loadingActions.setLoading(true));
          const result = await getQuote(id);

          if (result?.financialProduct?.offers) {
            const statusId = result?.financialProduct?.offers[0].statusId ?? '';
            setQuoteStatus(
              Object.entries(offerStatusIdOptionsList).find(([_statusId]) => _statusId === statusId)?.[1].label ||
                statusId
            );
            setQuote(result?.financialProduct?.offers[0]);
          }

          store.dispatch(loadingActions.setLoading(false));
        }
      };

      getQuoteData().catch(() => {
        navigate.error();
        store.dispatch(loadingActions.setLoading(false));
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id]
  );

  const navigateBack = useCallback(() => {
    switch (sourceType) {
      case DashboardSubpageDocumentsSourceType.ACCEPTED:
        navigate.dashboardAccepted();
        break;
      case DashboardSubpageDocumentsSourceType.QUOTATION_SUMMARY:
        navigate.dashboardQuotes();
        break;
      case DashboardSubpageDocumentsSourceType.RUNNING:
        navigate.dashboardRun();
        break;
      case DashboardSubpageDocumentsSourceType.STUDIES:
        navigate.dashboardStudies();
        break;
      default:
        navigate.dashboardHome();
    }
  }, [navigate, sourceType]);

  return (
    <PageWrap>
      <Heading level={1}>
        {t('dashboardSubpageDocuments:pageHeader', {
          id: getId(),
          status: [DashboardSubpageDocumentsSourceType.RUNNING, DashboardSubpageDocumentsSourceType.ACCEPTED].includes(
            sourceType
          )
            ? contractStatus
            : quoteStatus,
        })}
      </Heading>

      <Card title="" className="u-mb u-text-left">
        <Layout>
          <Layout.Item default="1/1">
            <Heading level={5}>{t('dashboardSubpageDocuments:pageSubheader')}</Heading>
          </Layout.Item>
          <Layout.Item default="1/1">
            <DashboardDocumentLeasingDocumentsTable
              entityType={DashboardSubpageDocumentsEntityType.LEASING_DOCUMENTS}
              sourceType={sourceType}
              selectedId={leasingDocumentsSelectedId || ''}
              transactionId={transactionId || ''}
            />
          </Layout.Item>
        </Layout>
      </Card>

      {sourceType !== DashboardSubpageDocumentsSourceType.QUOTATION_SUMMARY && (
        <DashboardDocumentUpload
          id={id || ''}
          sourceType={sourceType}
          entityType={DashboardSubpageDocumentsEntityType.CUSTOMER_DOCUMENTS}
        />
      )}

      {[DashboardSubpageDocumentsSourceType.ACCEPTED, DashboardSubpageDocumentsSourceType.RUNNING].includes(
        sourceType
      ) && (
        <DashboardDocumentUpload
          id={contractId || ''}
          sourceType={sourceType}
          entityType={DashboardSubpageDocumentsEntityType.DEALER_DOCUMENTS}
        />
      )}

      <Layout center className="u-mt">
        <Layout.Item default="4/12">
          <Button full type="button" onClick={() => navigateBack()}>
            {t('dashboardSubpageDocuments:navigateBackButton')}
          </Button>
        </Layout.Item>
      </Layout>
    </PageWrap>
  );
};
